import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Plus, 
  Settings,
  Table,
  Trash2,
  Edit,
  LogOut,
  CheckCircle,
  Crown
} from 'lucide-react';

const TischVorlagen = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    is_default: false
  });
  const [tische, setTische] = useState([{ tischnummer: '', kapazitaet: '' }]);
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    fetchVorlagen();
  }, []);

  const fetchVorlagen = async () => {
    try {
      const response = await axios.get('/tischvorlagen');
      setVorlagen(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVorlage = async (e) => {
    e.preventDefault();
    
    if (!formData.name || tische.some(t => !t.tischnummer || !t.kapazitaet)) {
      toast.error('Bitte fuellen Sie alle Felder aus');
      return;
    }

    setCreateLoading(true);
    
    try {
      const response = await axios.post('/tischvorlagen', {
        ...formData,
        tische: tische.map(t => ({
          tischnummer: t.tischnummer,
          kapazitaet: parseInt(t.kapazitaet)
        }))
      });
      
      setVorlagen([response.data, ...vorlagen]);
      setShowCreateDialog(false);
      setFormData({ name: '', beschreibung: '', is_default: false });
      setTische([{ tischnummer: '', kapazitaet: '' }]);
      toast.success('Tischvorlage erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Vorlage');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteVorlage = async (vorlageId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Vorlage loeschen moechten?')) {
      return;
    }

    try {
      await axios.delete(`/tischvorlagen/${vorlageId}`);
      setVorlagen(vorlagen.filter(v => v.id !== vorlageId));
      toast.success('Vorlage erfolgreich geloescht');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Fehler beim Loeschen der Vorlage');
    }
  };

  const addTisch = () => {
    setTische([...tische, { tischnummer: '', kapazitaet: '' }]);
  };

  const removeTisch = (index) => {
    if (tische.length > 1) {
      setTische(tische.filter((_, i) => i !== index));
    }
  };

  const updateTisch = (index, field, value) => {
    const updatedTische = tische.map((tisch, i) => 
      i === index ? { ...tisch, [field]: value } : tisch
    );
    setTische(updatedTische);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
                data-testid="back-to-dashboard-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Dashboard</span>
              </Button>
              <div>
                <h1 className="text-xl font-bold font-space-grotesk text-gray-900">
                  Tischvorlagen
                </h1>
                <p className="text-sm text-gray-600">Verwalten Sie Ihre Tischkonfigurationen</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.email}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {user.role === 'admin' ? 'Administrator' : 'Hostess'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Controls Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tischkonfigurationen</h2>
              <p className="text-gray-600">
                Erstellen Sie Vorlagen, die automatisch bei neuen Events verwendet werden
              </p>
            </div>
            
            {user.role === 'admin' && (
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium"
                    data-testid="create-template-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Neue Vorlage erstellen
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="create-template-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-gray-900">
                      Neue Tischvorlage erstellen
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                      Diese Tische werden automatisch bei jedem neuen Event erstellt.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateVorlage} className="space-y-6 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                        Vorlagenname *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="z.B. Standard VIP Layout"
                        className="transition-all duration-200 focus:ring-2 focus:ring-blue-500/20"
                        data-testid="template-name-input"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="beschreibung" className="text-sm font-medium text-gray-700">
                        Beschreibung (optional)
                      </Label>
                      <Textarea
                        id="beschreibung"
                        value={formData.beschreibung}
                        onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                        placeholder="Beschreibung der Tischkonfiguration..."
                        rows={2}
                        className="resize-none transition-all duration-200 focus:ring-2 focus:ring-blue-500/20"
                        data-testid="template-description-input"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_default"
                        checked={formData.is_default}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                        data-testid="template-default-switch"
                      />
                      <Label htmlFor="is_default" className="text-sm font-medium text-gray-700">
                        Als Standardvorlage festlegen
                      </Label>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-gray-700">Tische *</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addTisch}
                          className="flex items-center space-x-1"
                          data-testid="add-table-button"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Tisch hinzufuegen</span>
                        </Button>
                      </div>
                      
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {tische.map((tisch, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <Input
                                placeholder="Tischnummer (z.B. VIP-1)"
                                value={tisch.tischnummer}
                                onChange={(e) => updateTisch(index, 'tischnummer', e.target.value)}
                                className="text-sm"
                                data-testid={`table-number-${index}`}
                              />
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                min="1"
                                placeholder="Kapazitaet"
                                value={tisch.kapazitaet}
                                onChange={(e) => updateTisch(index, 'kapazitaet', e.target.value)}
                                className="text-sm"
                                data-testid={`table-capacity-${index}`}
                              />
                            </div>
                            {tische.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeTisch(index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`remove-table-${index}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCreateDialog(false)}
                        disabled={createLoading}
                        data-testid="cancel-create-template-button"
                      >
                        Abbrechen
                      </Button>
                      <Button
                        type="submit"
                        disabled={createLoading}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                        data-testid="submit-create-template-button"
                      >
                        {createLoading ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Erstellen...
                          </div>
                        ) : (
                          'Vorlage erstellen'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        {vorlagen.length === 0 ? (
          <div className="text-center py-16 animate-fade-in" data-testid="no-templates-message">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mb-4">
              <Table className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Noch keine Vorlagen vorhanden</h3>
            <p className="text-gray-600 mb-6">
              Erstellen Sie Ihre erste Tischvorlage, um Zeit zu sparen.
            </p>
            {user.role === 'admin' && (
              <Button 
                onClick={() => setShowCreateDialog(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                data-testid="create-first-template-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erste Vorlage erstellen
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vorlagen.map((vorlage, index) => (
              <Card 
                key={vorlage.id}
                className="card transition-all duration-200 bg-white border-0 shadow-lg hover:shadow-xl animate-slide-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`template-card-${index}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                          {vorlage.name}
                        </CardTitle>
                        {vorlage.is_default && (
                          <Badge className="bg-amber-100 text-amber-700 flex items-center">
                            <Crown className="w-3 h-3 mr-1" />
                            Standard
                          </Badge>
                        )}
                      </div>
                      {vorlage.beschreibung && (
                        <CardDescription className="text-gray-600 text-sm mb-2 line-clamp-2">
                          {vorlage.beschreibung}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Anzahl Tische:</span>
                      <Badge className="bg-blue-100 text-blue-700">
                        {vorlage.tische?.length || 0} Tische
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      Gesamtkapazitaet: {vorlage.tische?.reduce((sum, t) => sum + (t.kapazitaet || 0), 0) || 0} Plaetze
                    </div>
                    
                    {user.role === 'admin' && (
                      <div className="flex justify-end space-x-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteVorlage(vorlage.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                          data-testid={`delete-template-${index}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TischVorlagen;